import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Split from "react-split";
import { makeStyles } from "@material-ui/core/styles";
import { Paper } from '@material-ui/core';

import { RootState } from '../redux/rootReducer';
import { RelationUserFolderDb, updateRelationUserFolder } from '../redux/relationUserFolderDbSlice';
import { UserCondition, updateUserCondition } from '../redux/userConditionSlice';
import useCurrentUserId from '../hooks/useCurrentUserId';
import useAllFoldersDb from '../hooks/useAllFoldersDb';
import useWindowSize from '@rehooks/window-size';

import { deepCopy } from '../general';
import Redirector from './Redirector';
import DigimagiAppBar from './DigimagiAppBar';
import FolderList from './FolderList';
import Folder from './Folder';

const useStyles = makeStyles(() => ({
  split: {
    display: 'flex',
    height: '100%',
    zIndex: 2,
    cursor: 'ew-resize',
    position: 'relative',
  },
  panelLeft: {
    flex: 'auto',
    overflow: 'hidden',
    zIndex: 0,
    cursor: 'auto',
    position: 'relative',
  },
  panelRight: {
    flex: 'auto',
    overflow: 'hidden',
    zIndex: 1,
    cursor: 'auto',
    position: 'relative',
  },
}));

type UserHomeProps = {
  redirected: boolean;
  setRedirected: (value: boolean) => void;
  setAppBarFrom: (from: '/' | '/book') => void;
  userConditionId: string | undefined;
  userCondition: UserCondition | undefined;
}
const UserHome: React.FC<UserHomeProps> = ({ redirected, setRedirected, setAppBarFrom, userConditionId, userCondition }) => {
  const dispatch = useDispatch();
  const relationUserFolders = useSelector((state: RootState) => state.relationUserFolderDb).relationUserFolders;
  const userId = useCurrentUserId();
  const [splitRatios, setSplitRatios] = useState<number[]>([50,50]);
  const [folderIds, folders] = useAllFoldersDb(userId);
  const classes = useStyles();
  const windowSize = useWindowSize();

  const getRuf = (folderId: string): [string, RelationUserFolderDb] => {
    const id = Object.keys(relationUserFolders).filter(id => {
      const target = relationUserFolders[id];
      return target.folderId === folderId && target.userId === userId;
    })[0];
    const value = relationUserFolders[id];
    return [id, value];
  }
  const updateFolderOrder = (folderId: string) => {
    const [targetId, target] = getRuf(folderId);
    const ids = folderIds;
    ids.forEach(id => {
      const [rufId, ruf] = getRuf(id);
      if (rufId === targetId) return;
      else if (ruf.order > target.order) dispatch(updateRelationUserFolder({ id: rufId, order: ruf.order - 1 }));
    })
  }

  const dragSplit = (sizes: number[]) => {
    if (!userConditionId || !userCondition) return;
    const newRatio = deepCopy(userCondition.splitRatios);
    newRatio.userHome = sizes;
    dispatch(updateUserCondition({ id: userConditionId, splitRatios: newRatio }));
    setSplitRatios(newRatio.userHome);
  }

  useEffect(() => {
    if (!userCondition) return;
    setSplitRatios(userCondition.splitRatios.userHome);
  }, [userCondition])

  if (!redirected) return <Redirector doneRedirect={() => setRedirected(true)} />;
  return (
    <>
      <DigimagiAppBar setRedirected={setRedirected} parent='UserHome' setAppBarFrom={() => setAppBarFrom('/')} />
      <Paper style={{ height: windowSize.innerHeight - 64 }}>
        <Split
          minSize={450}
          expandToMin={false}
          gutterSize={5}
          gutterAlign="center"
          snapOffset={30}
          dragInterval={1}
          direction="horizontal"
          cursor="col-resize"
          sizes={[Number.isFinite(splitRatios[0]) ? splitRatios[0] : 50, Number.isFinite(splitRatios[1]) ? splitRatios[1] : 50]}
          onDrag={(sizes: number[]) => dragSplit(sizes)}
          className={classes.split}
        >
          <div className={classes.panelLeft}>
            <FolderList folders={folders} folderIds={folderIds} />
          </div>
          <div className={classes.panelRight}>
            <Folder updateFolderOrder={updateFolderOrder} />
          </div>
        </Split>
      </Paper>
    </>
  );
}

export default UserHome;
